import {
    BaseRenderer,
    type CompModel,
    type IAssetsLoader,
    type ICompositor,
    MultiPageVideoModel,
} from '@bynder-studio/render-core';
import type { IPlaybackRenderer, LoopInterval } from './IPlaybackRenderer';
import { Playback } from '../Playback/Playback';

export class PlaybackRenderer extends BaseRenderer<MultiPageVideoModel> implements IPlaybackRenderer {
    isPlaying = false;

    protected playback: Playback;

    constructor(
        videoModel: MultiPageVideoModel,
        assetLoader: IAssetsLoader,
        compositor: ICompositor,
        public loopInterval: LoopInterval,
    ) {
        super(videoModel, assetLoader, compositor);
        this.playback = new Playback(this, loopInterval);
    }

    async init(): Promise<void> {
        super.init();
        await this.playback.init();
    }

    setCurrentFrame(frameIndex: number) {
        return this.playback.setCurrentFrame(frameIndex);
    }

    getCurrentFrame(): number {
        return this.playback.getCurrentFrame();
    }

    getPlaybackDuration() {
        return this.playback.getPlaybackDuration();
    }

    startPlayback() {
        this.playback.startPlayback();
    }

    pausePlayback() {
        this.playback.pausePlayback();
    }

    getIsPlaying = () => {
        return this.playback.getIsPlaying();
    };

    redraw() {
        const compModel = this.creativeModel.getCompModel(this.playback.frameIndex);
        this.compositor.drawCompModel(compModel);
    }

    drawCompModel(compModel: CompModel): Promise<boolean> {
        return this.playback.drawCompModel(compModel);
    }

    getScale(): number {
        return this.compositor.getScale();
    }

    setScale(scale: number) {
        this.compositor.setScale(scale);
        this.eventEmitter.emit('scaleUpdated', { scale });
        this.redraw();

        return this;
    }

    zoomToFit() {
        const dimension = this.compositor.getCompositorWrapperDimension();
        const creativeDimension = this.creativeModel.getDimensions();
        const scaleX = dimension.getWidth() / creativeDimension.getWidth();
        const scaleY = dimension.getHeight() / creativeDimension.getHeight();
        const scale = Math.min(scaleX, scaleY);

        this.setScale(scale);
    }
}
